<template>
  <div class="public-deliverable-assets">
    <v-app-bar app color="appBarBgColor" dark clipped-left>
      <v-toolbar-title class="title-img">
        <v-img
          :src="appLogo"
          height="35px"
          width="200px"
          contain
        />
      </v-toolbar-title>
    </v-app-bar>

    <div v-if="ready" class="public-breadcrumb-container">
      <v-row no-gutters class="public-breadcrumb-row align-center">
        <v-col class="text-start">
          <Breadcrumbs :items="breadcrumbs" />
        </v-col>

        <v-col cols="auto" class="text-end">
          <v-btn text small @click="toggleAll">
            <v-icon left>{{ allDeliverablesExpanded ? 'mdi-unfold-less-horizontal' : 'mdi-unfold-more-horizontal' }}</v-icon>
            {{ allDeliverablesExpanded ? $t('close_all') : $t('open_all') }}
          </v-btn>
        </v-col>
      </v-row>
    </div>

    <v-progress-linear v-if="loading" indeterminate color="primary" />

    <v-alert v-else-if="error" type="error" outlined class="ma-6">
      {{ error }}
    </v-alert>

    <DeliverableAssetsManagement
      v-else-if="ready"
      ref="deliverableAssets"
      :job-id="jobId"
      :job-data="jobData"
      :public-mode="true"
      @expanded-change="allDeliverablesExpanded = $event"
    />
  </div>
</template>

<script>
import { mapMutations } from 'vuex'
import api from '@/services/api'
import AppMutations from '@/store/app/mutations-types'
import DeliverableAssetsManagement from '@/components/job/DeliverableAssetsManagement.vue'
import JobAPI from '@/services/api/job/job'
import Breadcrumbs from '@/components/Breadcrumbs.vue'

export default {
  name: 'PublicDeliverableAssets',

  components: {
    DeliverableAssetsManagement,
    Breadcrumbs
  },

  props: {
    appId: {
      type: [String, Number],
      required: true
    },
    jobId: {
      type: [String, Number],
      required: true
    },
    contentAppId: {
      type: [String, Number],
      required: true
    },
    businessAppId: {
      type: [String, Number],
      required: true
    }
  },

  data() {
    return {
      loading: true,
      ready: false,
      error: null,
      jobData: {},
      allDeliverablesExpanded: false,
      previousApplicationState: null
    }
  },

  mounted() {
    this.initialize()
  },

  beforeDestroy() {
    // A signed-in user can also open a public link. Restore their application
    // context when leaving so the public token never changes their session.
    if (!this.previousApplicationState) return

    this[AppMutations.SET_USER_APPLICATIONS](this.previousApplicationState.applications)
    this[AppMutations.SET_SELECTED_JOB_APP](this.previousApplicationState.jobAppId)
    this[AppMutations.SET_SELECTED_CONTENT_APP](this.previousApplicationState.contentAppId)
    this[AppMutations.SET_SELECTED_BUSINESS_APP](this.previousApplicationState.businessAppId)
  },

  computed: {
    appLogo() {
      if (this.$vuetify.theme.themes.light.logo) {
        return this.$vuetify.theme.themes.light.logo
      }

      return require('@/assets/logo_jamworks.svg')
    },

    breadcrumbs() {
      return [
        { text: this.jobData.job_title || 'Job', ripple: false },
        { text: this.$t('deliverable_assets'), ripple: false }
      ]
    }
  },

  methods: {
    ...mapMutations('app', [
      AppMutations.SET_USER_APPLICATIONS,
      AppMutations.SET_SELECTED_JOB_APP,
      AppMutations.SET_SELECTED_CONTENT_APP,
      AppMutations.SET_SELECTED_BUSINESS_APP
    ]),

    async initialize() {
      const scopedToken = this.$route.query['scoped-token']
      if (!scopedToken) {
        this.error = 'This public link is missing its scoped token.'
        this.loading = false
        return
      }

      try {
        // The API returns only the instances granted by this scoped token.
        // These instances provide the service URLs used by the job, content,
        // and business API clients.
        const response = await api.core.application_instance.getUserAppInstances()
        const applications = response.data.items || []
        const hasInstance = (id, key) => applications.some(application =>
          String(application.application_instance_id) === String(id) &&
          application.application_key === key
        )

        if (!hasInstance(this.appId, 'job') ||
          !hasInstance(this.contentAppId, 'content') ||
          !hasInstance(this.businessAppId, 'business')) {
          throw new Error('This scoped token does not grant access to this deliverable.')
        }

        this.previousApplicationState = {
          applications: this.$store.state.app.userApplications,
          jobAppId: this.$store.state.app.selectedJobApp,
          contentAppId: this.$store.state.app.selectedContentApp,
          businessAppId: this.$store.state.app.selectedBusinessApp
        }

        this[AppMutations.SET_USER_APPLICATIONS](applications)
        this[AppMutations.SET_SELECTED_JOB_APP](this.appId)
        this[AppMutations.SET_SELECTED_CONTENT_APP](this.contentAppId)
        this[AppMutations.SET_SELECTED_BUSINESS_APP](this.businessAppId)
        const jobResponse = await JobAPI.getJob(this.jobId)
        this.jobData = jobResponse.data || {}
        this.ready = true
      } catch (error) {
        this.error = error.response?.data?.message || error.message || 'This public link is invalid or has expired.'
      } finally {
        this.loading = false
      }
    },

    toggleAll() {
      this.$refs.deliverableAssets?.toggleAll()
    }
  }
}
</script>

<style scoped>
.public-deliverable-assets {
  min-height: 100vh;
  background-color: #f5f5f5;
}

.title-img {
  padding-left: 10px !important;
}

.public-breadcrumb-container {
  padding: 0 22px;
}

.public-breadcrumb-row {
  min-height: 74px;
}
</style>
